import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import apiToken, { handleApiError } from '@/shared/services/axiosInstance';
import { ProductCardData } from '@/shared/types/ProductCardTypes';
import { useQueryParams } from '@/shared/hooks/useQueryParams';
import { useDropDown } from '@/shared/hooks/useDropDown';

export const useProductList = () => {
  // State management
  const [product, setProduct] = useState<ProductCardData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stockStatus, setStockStatus] = useState('all');
  const [sort, setSort] = useState('latest');
  const stockDropdown = useDropDown();
  const sortDropdown = useDropDown();

  // Router hooks
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { getAllParams, getParam, setParam, setAllParams } = useQueryParams();

  // API call
  const getProductList = useCallback(async () => {
    const params = getAllParams(
      ['page', 'sort', 'stockStatus', 'artistId', 'tag'],
      { page: '1', sort: 'latest', stockStatus: 'all' },
    );
    const {
      page,
      sort: currentSort,
      stockStatus: currentStockStatus,
      artistId,
      tag,
    } = params;

    let queryParam = '';
    if (artistId) {
      queryParam = `artistId=${artistId}`;
    } else if (tag) {
      queryParam = `tag=${tag}`;
    } else if (id && id !== '0') {
      queryParam = `categoryId=${id}`;
    }

    const baseUrl = `/api/v1/product/list?page=${page}&limit=20&sort=${currentSort}&stockStatus=${currentStockStatus}`;
    const finalUrl = queryParam ? `${baseUrl}&${queryParam}` : baseUrl;

    try {
      const response = await apiToken.get(finalUrl);
      setProduct(response.data.data.productOptionList);
      setTotalCount(Math.ceil(response.data.data.totalCount / 20));
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch product list:', errorMessage);
      alert(
        'Failed to load products. Please refresh the page or try again later.',
      );
    }
  }, [location.search, id, getAllParams]);

  const getSearchProductList = useCallback(async () => {
    const {
      page,
      sort: currentSort,
      stockStatus: currentStockStatus,
      keyword,
    } = getAllParams(['page', 'sort', 'stockStatus', 'keyword'], {
      page: '1',
      sort: 'latest',
      stockStatus: 'all',
      keyword: '',
    });

    const baseUrl = `/api/v1/product/search?page=${page}&limit=20&sort=${currentSort}&stockStatus=${currentStockStatus}&keyword=${keyword}`;

    try {
      const response = await apiToken.get(baseUrl);
      setProduct(response.data.data.productOptionList);
      setTotalCount(Math.ceil(response.data.data.totalCount / 20));
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch search product list:', errorMessage);
      alert(
        'Failed to load search results. Please check your connection and try again.',
      );
    }
  }, [location.search, getAllParams]);

  // Event handlers
  const handleStockStatusChange = (status: string) => {
    setStockStatus(status);
    stockDropdown.close();
    setParam('stockStatus', status);
  };

  const handleSortChange = (sortValue: string) => {
    setSort(sortValue);
    sortDropdown.close();
    setParam('sort', sortValue);
  };

  // Utility functions
  const getCategoryName = () => {
    return getParam('categoryName') || getParam('artistName');
  };

  const getKeywordName = () => {
    return getParam('keyword');
  };

  const getStockStatusLabel = (status: string) => {
    switch (status) {
      case 'all':
        return 'ALL';
      case 'inStock':
        return 'In stock';
      case 'outOfStock':
        return 'Out of stock';
      default:
        return 'ALL';
    }
  };

  const getSortLabel = (sortValue: string) => {
    switch (sortValue) {
      case 'latest':
        return 'Latest';
      case 'bestSelling':
        return 'Best Selling';
      default:
        return 'Latest';
    }
  };

  useEffect(() => {
    const defaults = { page: '1', sort: 'latest', stockStatus: 'all' };
    const hasAllDefaults =
      getParam('page') && getParam('sort') && getParam('stockStatus');

    if (!hasAllDefaults) {
      const completedParams = getAllParams(
        ['page', 'sort', 'stockStatus'],
        defaults,
      );
      setAllParams(completedParams, { resetPage: false, replace: true });
      return;
    }

    const { sort: querySort, stockStatus: queryStockStatus } = getAllParams(
      ['sort', 'stockStatus'],
      defaults,
    );

    setSort(querySort);
    setStockStatus(queryStockStatus);

    if (location.pathname === '/search') {
      getSearchProductList();
    } else {
      getProductList();
    }
  }, [
    location.search,
    location.pathname,
    id,
    getAllParams,
    setAllParams,
    getProductList,
    getSearchProductList,
    getParam,
  ]);

  return {
    // State
    product,
    totalCount,
    stockStatus,
    sort,
    stockDropdown,
    sortDropdown,

    // Event handlers
    handleStockStatusChange,
    handleSortChange,

    // Utility functions
    getKeywordName,
    getCategoryName,
    getStockStatusLabel,
    getSortLabel,
  };
};
